import type { TodoDueKind, TodoNoteItemView } from '../types/todoNote';
import { compareTodoFavoriteFirst } from './todoNoteFavorite';

export type TodoPostitPosition = { x: number; y: number };

export type TodoPostitGridMetrics = {
  cols: number;
  cellWidth: number;
  cellHeight: number;
  gap: number;
  pad: number;
};

export type TodoPostitTile = {
  /** 보드 좌표 키. 루틴 더미는 부모 id로 고정해 날짜가 바뀌어도 자리를 유지한다. */
  id: string;
  item: TodoNoteItemView;
  stackCount: number;
  isRoutineStack: boolean;
};

export const TODO_POSTIT_MIN_CARD_WIDTH = 200;
export const TODO_POSTIT_CARD_WIDTH = TODO_POSTIT_MIN_CARD_WIDTH;
export const TODO_POSTIT_CARD_MIN_HEIGHT = 152;
export const TODO_POSTIT_GRID_GAP = 16;
export const TODO_POSTIT_PAD = 16;
export const TODO_POSTIT_STACK_PEEK = 8;
export const TODO_POSTIT_STACK_LAYER_MAX = 2;
export const TODO_POSTIT_LAYOUT_VERSION = 'v3';

export function todoPostitStackExtra(stackCount: number, isRoutineStack: boolean): number {
  if (!isRoutineStack) return 0;
  return Math.min(TODO_POSTIT_STACK_LAYER_MAX, Math.max(0, stackCount - 1)) * TODO_POSTIT_STACK_PEEK;
}

export function todoPostitLayoutStorageKey(boardId: string): string {
  return `cp.todoNote.postitLayout.${TODO_POSTIT_LAYOUT_VERSION}.${boardId.trim()}`;
}

export function formatTodoPostitDate(dueKind: TodoDueKind, dueDate: string): string {
  const parts = dueDate.trim().split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1) return '';
  if (dueKind === 'month') return `${year}년 ${month}월`;
  const d = Number.isFinite(day) && day > 0 ? day : 1;
  return `${year}년 ${month}월 ${d}일`;
}

function hasPostitText(item: TodoNoteItemView): boolean {
  return (
    (item.title ?? '').trim().length > 0 ||
    (item.body ?? '').trim().length > 0 ||
    (item.misc ?? '').trim().length > 0
  );
}

export function todoPostitGridMetrics(boardWidth: number): TodoPostitGridMetrics {
  const gap = TODO_POSTIT_GRID_GAP;
  const pad = TODO_POSTIT_PAD;
  const minW = TODO_POSTIT_MIN_CARD_WIDTH;
  const usable = Math.max(minW, boardWidth - pad * 2);
  const cols = Math.max(1, Math.floor((usable + gap) / (minW + gap)));
  const cellWidth = cols <= 1 ? usable : (usable - gap * (cols - 1)) / cols;
  const cellHeight =
    TODO_POSTIT_CARD_MIN_HEIGHT + TODO_POSTIT_STACK_PEEK * TODO_POSTIT_STACK_LAYER_MAX;
  return { cols, cellWidth, cellHeight, gap, pad };
}

export type TodoPostitBox = { x: number; y: number; w: number; h: number };

export function todoPostitCategoryRankById(
  categories: { id: string; name: string; sort_order: number }[]
): Map<string, number> {
  const sorted = [...categories].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
  );
  const map = new Map<string, number>();
  sorted.forEach((c, i) => map.set(c.id, i));
  return map;
}

function compareTodoPostitTiles(
  a: TodoPostitTile,
  b: TodoPostitTile,
  categoryRankById?: Map<string, number>
): number {
  const fav = compareTodoFavoriteFirst(a.item, b.item);
  if (fav !== 0) return fav;
  const ra = categoryRankById?.get((a.item.category_id ?? '').trim()) ?? Number.POSITIVE_INFINITY;
  const rb = categoryRankById?.get((b.item.category_id ?? '').trim()) ?? Number.POSITIVE_INFINITY;
  if (ra !== rb) return ra - rb;
  return (
    a.item.due_date.localeCompare(b.item.due_date) ||
    (a.item.bucket_order ?? 0) - (b.item.bucket_order ?? 0) ||
    a.id.localeCompare(b.id)
  );
}

export function todoPostitHeightOf(id: string, heightById?: Record<string, number>): number {
  const h = heightById?.[id];
  return typeof h === 'number' && h > 0 ? h : TODO_POSTIT_CARD_MIN_HEIGHT;
}

export function todoPostitColumnX(col: number, metrics: TodoPostitGridMetrics): number {
  return metrics.pad + col * (metrics.cellWidth + metrics.gap);
}

export function todoPostitColFromX(x: number, metrics: TodoPostitGridMetrics): number {
  const stepX = metrics.cellWidth + metrics.gap;
  return Math.max(0, Math.min(metrics.cols - 1, Math.round((x - metrics.pad) / stepX)));
}

export function todoPostitBoxesOverlap(a: TodoPostitBox, b: TodoPostitBox, gap = 0): boolean {
  return (
    a.x < b.x + b.w + gap &&
    a.x + a.w + gap > b.x &&
    a.y < b.y + b.h + gap &&
    a.y + a.h + gap > b.y
  );
}

export function placeTodoPostitWithoutOverlap(params: {
  x: number;
  y: number;
  h: number;
  others: TodoPostitBox[];
  boardWidth: number;
}): TodoPostitPosition {
  const metrics = todoPostitGridMetrics(params.boardWidth);
  const col = todoPostitColFromX(params.x, metrics);
  const x = todoPostitColumnX(col, metrics);
  let y = Math.max(metrics.pad, params.y);
  const boxW = metrics.cellWidth;
  const boxH = Math.max(TODO_POSTIT_CARD_MIN_HEIGHT, params.h);
  let guard = 0;
  while (guard < 500) {
    guard += 1;
    const box: TodoPostitBox = { x, y, w: boxW, h: boxH };
    let pushTo = y;
    let hit = false;
    for (let i = 0; i < params.others.length; i += 1) {
      const other = params.others[i];
      if (!todoPostitBoxesOverlap(box, other, metrics.gap)) continue;
      hit = true;
      pushTo = Math.max(pushTo, other.y + other.h + metrics.gap);
    }
    if (!hit) break;
    y = pushTo <= y ? y + boxH + metrics.gap : pushTo;
  }
  return { x, y };
}

function boxesFromPositions(
  ids: string[],
  positions: Record<string, TodoPostitPosition>,
  heightById: Record<string, number> | undefined,
  boardWidth: number,
  excludeId?: string
): TodoPostitBox[] {
  const metrics = todoPostitGridMetrics(boardWidth);
  const out: TodoPostitBox[] = [];
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    if (excludeId && id === excludeId) continue;
    const pos = positions[id];
    if (!pos) continue;
    out.push({
      x: pos.x,
      y: pos.y,
      w: metrics.cellWidth,
      h: todoPostitHeightOf(id, heightById),
    });
  }
  return out;
}

export function layoutTodoPostitGrid(
  ids: string[],
  boardWidth: number,
  options?: {
    originY?: number;
    heightById?: Record<string, number>;
    /** 같은 키끼리 한 덩어리로 두고, 키가 바뀌면 다음 행부터 붙인다 */
    groupKeyById?: Record<string, string>;
  }
): Record<string, TodoPostitPosition> {
  const metrics = todoPostitGridMetrics(boardWidth);
  const originY = options?.originY ?? metrics.pad;
  const colY: number[] = [];
  for (let c = 0; c < metrics.cols; c += 1) colY.push(originY);
  const out: Record<string, TodoPostitPosition> = {};
  let prevGroup: string | undefined;
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    const group = options?.groupKeyById?.[id];
    if (i > 0 && group !== undefined && group !== prevGroup) {
      const nextY = Math.max(...colY);
      for (let c = 0; c < colY.length; c += 1) colY[c] = nextY;
    }
    prevGroup = group;
    const h = todoPostitHeightOf(id, options?.heightById);
    let best = 0;
    for (let c = 1; c < metrics.cols; c += 1) {
      if (colY[c] < colY[best]) best = c;
    }
    out[id] = { x: todoPostitColumnX(best, metrics), y: colY[best] };
    colY[best] += h + metrics.gap;
  }
  return out;
}

export function resolveTodoPostitPositions(params: {
  ids: string[];
  saved: Record<string, TodoPostitPosition>;
  boardWidth: number;
  heightById?: Record<string, number>;
}): Record<string, TodoPostitPosition> {
  const metrics = todoPostitGridMetrics(params.boardWidth);
  const ordered = params.ids.slice().sort((a, b) => {
    const pa = params.saved[a];
    const pb = params.saved[b];
    if (pa && pb) return pa.y - pb.y || pa.x - pb.x || a.localeCompare(b);
    if (pa) return -1;
    if (pb) return 1;
    return params.ids.indexOf(a) - params.ids.indexOf(b);
  });
  const placed: TodoPostitBox[] = [];
  const out: Record<string, TodoPostitPosition> = {};
  for (let i = 0; i < ordered.length; i += 1) {
    const id = ordered[i];
    const saved = params.saved[id];
    const h = todoPostitHeightOf(id, params.heightById);
    const pos = placeTodoPostitWithoutOverlap({
      x: saved?.x ?? metrics.pad,
      y: saved?.y ?? metrics.pad,
      h,
      others: placed,
      boardWidth: params.boardWidth,
    });
    placed.push({ x: pos.x, y: pos.y, w: metrics.cellWidth, h });
    out[id] = pos;
  }
  return out;
}

export function assignTodoPostitCell(params: {
  id: string;
  x: number;
  y: number;
  positions: Record<string, TodoPostitPosition>;
  ids: string[];
  boardWidth: number;
  heightById?: Record<string, number>;
}): Record<string, TodoPostitPosition> {
  const pos = placeTodoPostitWithoutOverlap({
    x: params.x,
    y: params.y,
    h: todoPostitHeightOf(params.id, params.heightById),
    others: boxesFromPositions(
      params.ids,
      params.positions,
      params.heightById,
      params.boardWidth,
      params.id
    ),
    boardWidth: params.boardWidth,
  });
  return {
    ...params.positions,
    [params.id]: pos,
  };
}

export function mergeTodoPostitPositions(params: {
  ids: string[];
  saved: Record<string, TodoPostitPosition>;
  boardWidth: number;
  heightById?: Record<string, number>;
  groupKeyById?: Record<string, string>;
}): Record<string, TodoPostitPosition> {
  const hasSaved = params.ids.some((id) => Boolean(params.saved[id]));
  if (!hasSaved) {
    return layoutTodoPostitGrid(params.ids, params.boardWidth, {
      heightById: params.heightById,
      groupKeyById: params.groupKeyById,
    });
  }
  return resolveTodoPostitPositions(params);
}

export function buildTodoPostitTiles(
  items: TodoNoteItemView[],
  todayYmd: string,
  categoryRankById?: Map<string, number>
): TodoPostitTile[] {
  const regular: TodoPostitTile[] = [];
  const groups = new Map<string, TodoNoteItemView[]>();
  for (const it of items) {
    if (it.completed) continue;
    if (!hasPostitText(it)) continue;
    const parentId = it.routineParentId?.trim();
    if (parentId) {
      const list = groups.get(parentId) ?? [];
      list.push(it);
      groups.set(parentId, list);
      continue;
    }
    regular.push({ id: it.id, item: it, stackCount: 1, isRoutineStack: false });
  }
  const routineTiles: TodoPostitTile[] = [];
  Array.from(groups.entries()).forEach(([parentId, occs]) => {
    const future = occs
      .filter((o) => !o.completed && o.due_date >= todayYmd)
      .slice()
      .sort((a, b) => a.due_date.localeCompare(b.due_date) || a.id.localeCompare(b.id));
    const front = future[0];
    if (!front) return;
    routineTiles.push({
      id: parentId,
      item: front,
      stackCount: future.length,
      isRoutineStack: future.length > 1,
    });
  });
  return [...regular, ...routineTiles].sort((a, b) =>
    compareTodoPostitTiles(a, b, categoryRankById)
  );
}

export function parseTodoPostitLayout(raw: unknown): Record<string, TodoPostitPosition> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, TodoPostitPosition> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([id, value]) => {
    if (!id || !value || typeof value !== 'object' || Array.isArray(value)) return;
    const x = Number((value as { x?: unknown }).x);
    const y = Number((value as { y?: unknown }).y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    out[id] = { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  });
  return out;
}

export function loadTodoPostitLayout(boardId: string): Record<string, TodoPostitPosition> {
  if (!boardId.trim() || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(todoPostitLayoutStorageKey(boardId));
    if (!raw) return {};
    return parseTodoPostitLayout(JSON.parse(raw) as unknown);
  } catch {
    return {};
  }
}

export function saveTodoPostitLayout(
  boardId: string,
  positions: Record<string, TodoPostitPosition>
): void {
  if (!boardId.trim() || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(todoPostitLayoutStorageKey(boardId), JSON.stringify(positions));
  } catch {
    /* quota */
  }
}

const POSTIT_LAYOUT_SESSION_PREFIX = 'cp.todoNote.postitLayout.session.';

function postitLayoutSessionKey(userId: string, boardId: string): string {
  return `${POSTIT_LAYOUT_SESSION_PREFIX}${userId.trim()}.${boardId.trim()}`;
}

/** 이 탭이 열려 있는 동안 해당 보드 좌표를 DB에서 받은 적 있는지 (로그아웃해도 탭이 살아 있으면 유지) */
export function hasTodoPostitLayoutSessionSync(userId: string, boardId: string): boolean {
  if (!userId.trim() || !boardId.trim() || typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(postitLayoutSessionKey(userId, boardId)) === '1';
  } catch {
    return false;
  }
}

export function markTodoPostitLayoutSessionSync(userId: string, boardId: string): void {
  if (!userId.trim() || !boardId.trim() || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(postitLayoutSessionKey(userId, boardId), '1');
  } catch {
    /* quota */
  }
}
